interface GQLResponseError {
  message: string;
}

interface GQLResponseData<T> {
  data: T | null;
  errors?: GQLResponseError[];
}

/**
 * Adds authentication headers to a fetch request.
 */
export async function fetchWithAuth(
  url: string,
  props?: RequestInit,
  accessToken: string | null | undefined = null,
) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const sentProps: RequestInit = {
    ...props,
    headers: {
      ...headers,
      ...props?.headers,
    },
  };

  return await fetch(url, sentProps);
}

/**
 * Makes a GraphQL request to the Notifications Service with the expected T response type.
 */
export async function makeRequest<T>(
  url: string,
  query: string,
  variables: {
    [key: string]: string | Date | number | undefined | object | null | boolean;
  } = {},
  accessToken: string | null | undefined = null,
): Promise<GQLResponseData<T>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const response = await fetchWithAuth(
    url,
    {
      cache: "reload",
      method: "POST",
      headers,
      body: JSON.stringify({
        query,
        variables,
      }),
    },
    accessToken,
  );

  const responseData = (await response.json()) as GQLResponseData<T>;
  if (!response.ok) {
    let errorMessage = "Unknown error from Notifications Service";
    if (responseData.errors) {
      // Combine all error messages from the response and throw as a single error.
      errorMessage = responseData.errors
        .map((err: { message?: string }) => err.message)
        .filter(Boolean)
        .join("; ");
    }

    throw new NotificationsGraphQLRequestError(errorMessage);
  }

  return {
    data: responseData.data,
  };
}

// Custom error class for handling errors from Notifications GraphQL requests.
export class NotificationsGraphQLRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotificationsGraphQLRequestError";
  }
}
